/**
 * Tenant Connections vocabulary (ADR-57): the external accounts a sale
 * company connects so Marketing Studio can publish for it, and the one rule
 * the Studio needs from them - which channels can publish right now.
 *
 * Hierarchy: PLATFORM app credentials (a secret store, never a tenant row) ->
 * TENANT connection (an authorised external account) -> AUCTION default
 * destinations -> CAMPAIGN selected destinations. This file is vocabulary and
 * pure rules; storage and OAuth live in control-api.
 */

import { z } from 'zod';
import type { MarketingChannel } from './catalog.js';

export const CONNECTION_PROVIDERS = ['META', 'GOOGLE'] as const;
export const ConnectionProviderSchema = z.enum(CONNECTION_PROVIDERS);
export type ConnectionProvider = z.infer<typeof ConnectionProviderSchema>;

export const DESTINATION_TYPES = ['FACEBOOK_PAGE', 'INSTAGRAM_BUSINESS', 'YOUTUBE_CHANNEL', 'GA4_PROPERTY'] as const;
export const DestinationTypeSchema = z.enum(DESTINATION_TYPES);
export type DestinationType = z.infer<typeof DestinationTypeSchema>;

/** Connection health. Anything but CONNECTED is surfaced immediately in the
 *  Studio: a scheduled post must never quietly fail later. */
export const CONNECTION_STATUSES = ['CONNECTED', 'NEEDS_REAUTH', 'PERMISSION_MISSING', 'EXPIRED', 'DISCONNECTED', 'ERROR'] as const;
export const ConnectionStatusSchema = z.enum(CONNECTION_STATUSES);
export type ConnectionStatus = z.infer<typeof ConnectionStatusSchema>;

export interface ConnectionProviderDef {
  key: ConnectionProvider;
  /** The tenant-facing card title. */
  label: string;
  blurb: string;
  destinations: readonly DestinationType[];
}

export const CONNECTION_PROVIDER_DEFS: Readonly<Record<ConnectionProvider, ConnectionProviderDef>> = {
  META: {
    key: 'META',
    label: 'Facebook & Instagram',
    blurb: 'Publish approved campaign graphics to your Facebook Page and Instagram.',
    destinations: ['FACEBOOK_PAGE', 'INSTAGRAM_BUSINESS'],
  },
  GOOGLE: {
    key: 'GOOGLE',
    label: 'YouTube',
    blurb: 'Upload approved campaign videos to your channel and bring video analytics back.',
    destinations: ['YOUTUBE_CHANNEL', 'GA4_PROPERTY'],
  },
};

export const DESTINATION_TYPE_LABELS: Readonly<Record<DestinationType, string>> = {
  FACEBOOK_PAGE: 'Facebook Page',
  INSTAGRAM_BUSINESS: 'Instagram',
  YOUTUBE_CHANNEL: 'YouTube channel',
  GA4_PROPERTY: 'Google Analytics property',
};

/**
 * What each marketing channel needs before HipNumber can publish to it.
 * OWNED channels publish through HipNumber itself (the storefront, email/SMS);
 * EXPORT_ONLY channels have no publishing API worth trusting yet (the tenant
 * downloads and posts); the rest need a connected destination of a type.
 */
export type ChannelRequirement =
  | { kind: 'OWNED' }
  | { kind: 'EXPORT_ONLY' }
  | { kind: 'DESTINATION'; provider: ConnectionProvider; destination: DestinationType };

export const CHANNEL_REQUIREMENTS: Readonly<Record<MarketingChannel, ChannelRequirement>> = {
  FACEBOOK_FEED: { kind: 'DESTINATION', provider: 'META', destination: 'FACEBOOK_PAGE' },
  INSTAGRAM_FEED: { kind: 'DESTINATION', provider: 'META', destination: 'INSTAGRAM_BUSINESS' },
  REELS_TIKTOK: { kind: 'EXPORT_ONLY' },
  YOUTUBE: { kind: 'DESTINATION', provider: 'GOOGLE', destination: 'YOUTUBE_CHANNEL' },
  WEBSITE_HERO: { kind: 'OWNED' },
  EMAIL_SMS: { kind: 'OWNED' },
};

/** The minimal view of a tenant's connections the readiness rule reads. */
export interface ConnectionSummary {
  provider: ConnectionProvider;
  status: ConnectionStatus;
  destinations: readonly { type: DestinationType; enabled: boolean }[];
}

export const CHANNEL_READINESS = ['READY', 'CONNECTED', 'CONNECT', 'ATTENTION', 'EXPORT_ONLY', 'UNAVAILABLE'] as const;
export type ChannelReadiness = (typeof CHANNEL_READINESS)[number];

/** Which providers the PLATFORM has credentials for on this environment. A
 *  provider absent here cannot be connected by any tenant yet. */
export type ProvidersConfigured = Readonly<Partial<Record<ConnectionProvider, boolean>>>;

/**
 * Per channel: READY (owned), CONNECTED (a healthy enabled destination exists),
 * ATTENTION (a connection exists but is unhealthy or has no enabled
 * destination of the needed type), CONNECT (nothing connected), UNAVAILABLE
 * (HipNumber has not enabled the provider here), EXPORT_ONLY. Pure, so the
 * Create Campaign chips, the Studio banner and the publish gate all agree.
 */
export function channelReadiness(channel: MarketingChannel, connections: readonly ConnectionSummary[], configured?: ProvidersConfigured): ChannelReadiness {
  const req = CHANNEL_REQUIREMENTS[channel];
  if (req.kind === 'OWNED') return 'READY';
  if (req.kind === 'EXPORT_ONLY') return 'EXPORT_ONLY';
  const mine = connections.filter((c) => c.provider === req.provider && c.status !== 'DISCONNECTED');
  if (mine.length === 0) return configured && configured[req.provider] === false ? 'UNAVAILABLE' : 'CONNECT';
  const healthy = mine.some((c) => c.status === 'CONNECTED' && c.destinations.some((d) => d.type === req.destination && d.enabled));
  return healthy ? 'CONNECTED' : 'ATTENTION';
}

export const CHANNEL_READINESS_WORDS: Readonly<Record<ChannelReadiness, string>> = {
  READY: 'Ready',
  CONNECTED: 'Connected',
  CONNECT: 'Connect',
  ATTENTION: 'Needs attention',
  EXPORT_ONLY: 'Export only',
  UNAVAILABLE: 'Not available yet',
};

/** Which providers a tenant still has to connect for the channels it uses
 *  (providers the platform has not enabled are not the tenant's to fix). */
export function missingProviders(channels: readonly MarketingChannel[], connections: readonly ConnectionSummary[], configured?: ProvidersConfigured): ConnectionProvider[] {
  const out = new Set<ConnectionProvider>();
  for (const ch of channels) {
    const req = CHANNEL_REQUIREMENTS[ch];
    if (req.kind === 'DESTINATION' && channelReadiness(ch, connections, configured) === 'CONNECT') out.add(req.provider);
  }
  return [...out];
}
